import { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { UserRole } from "@/generated/types";
import { useCreateUser } from "../../../hooks/useCreateUser";

interface CreateUserDialogProps {
  onSuccess: () => void;
  children: React.ReactNode;
}

const ROLE_LABELS: Record<string, string> = {
  ADMIN: "Administrador",
  CLIENT: "Cliente",
  COLLABORATOR: "Colaborador",
};

export default function CreateUserDialog({ onSuccess, children }: CreateUserDialogProps) {
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [role, setRole] = useState<string>(UserRole.Client);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const { createUser, loading } = useCreateUser({
    onCompleted: () => {
      toast.success("Usuário criado com sucesso. Uma senha temporária foi enviada para o e-mail dele.");
      setEmail("");
      setFullName("");
      setRole(UserRole.Client);
      setFormErrors({});
      onSuccess();
    },
    onError: (error: any) => {
      const graphQLError = error.graphQLErrors?.[0];
      if (graphQLError?.extensions?.field_errors) {
        const fieldErrors: Record<string, string> = {};
        Object.entries(graphQLError.extensions.field_errors).forEach(([key, val]) => {
          fieldErrors[key] = String(val);
        });
        setFormErrors(fieldErrors);
      }
    },
  });

  const validate = useCallback((): boolean => {
    const errors: Record<string, string> = {};
    if (!email.trim()) {
      errors.email = "E-mail é obrigatório";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = "Por favor, insira um endereço de e-mail válido";
    }
    if (!fullName.trim()) {
      errors.fullName = "Nome completo é obrigatório";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [email, fullName]);

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    createUser(email, fullName, role);
  }, [validate, createUser, email, fullName, role]);

  return (
    <Dialog>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent maxWidth="max-w-lg">
        <DialogHeader>
          <DialogTitle>Adicionar Novo Usuário</DialogTitle>
          <DialogDescription>
            Criar uma nova conta de usuário. Uma senha temporária será enviada para o e-mail dele.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-5 mt-4">
          <div className="space-y-2">
            <Label htmlFor="create-email" className="text-xs uppercase tracking-widest text-primary-600 dark:text-primary-400">
              E-mail
            </Label>
            <Input
              id="create-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="rounded-none border-primary-200 dark:border-primary-800"
            />
            {formErrors.email && <p className="text-xs text-red-600 dark:text-red-400">{formErrors.email}</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="create-fullName" className="text-xs uppercase tracking-widest text-primary-600 dark:text-primary-400">
              Nome Completo
            </Label>
            <Input
              id="create-fullName"
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="rounded-none border-primary-200 dark:border-primary-800"
            />
            {formErrors.fullName && <p className="text-xs text-red-600 dark:text-red-400">{formErrors.fullName}</p>}
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-primary-600 dark:text-primary-400">
              Função
            </Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="rounded-none border-primary-200 dark:border-primary-800">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.values(UserRole).map((r) => (
                  <SelectItem key={r} value={r}>
                    {ROLE_LABELS[r] || r}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button
                type="button"
                variant="outline"
                className="rounded-none uppercase tracking-widest text-xs border-primary-200 dark:border-primary-800"
              >
                Cancelar
              </Button>
            </DialogClose>
            <Button
              type="submit"
              disabled={loading}
              className="rounded-none uppercase tracking-widest text-xs"
            >
              {loading ? "Criando..." : "Criar Usuário"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
